// Domain models derived from the backend (leazepro-backend/app/internal/repository/*/model.go).
// IDs are hex ObjectIDs, dates are ISO 8601 UTC strings, soft-deleted rows are simply absent.

import type { RawCapabilities } from '@/features/capabilities/capabilities'

export type ProfileType = 'landlord' | 'tenant'

export type Currency = 'INR' | (string & {})

export interface Address {
  firstLine: string
  secondLine?: string
  city: string
  state: string
  stateCode?: string
  country: string
  countryCode?: string
  postalCode: string
  status?: 'pending' | 'processing' | 'completed' | 'failed'
}

/** What `GET /auth/me` returns (auth/authenticated.go projection). */
export interface UserSession {
  id: string
  firstName: string
  lastName: string
  mobile: string
  email?: string
  avatar?: string
  currency?: Currency
  timeZone?: string
  defaultProfile: ProfileType
  profileCompleted?: boolean
  address?: Address
  activeSubscriptionId?: string
  pendingSubscriptionId?: string
  /** ISO2 of the country whose figures the dashboards show and the lists start on (LP-491); always set by the backend.
   * A viewing preference only: `address.countryCode` decides KYC, payouts and capabilities, never this. */
  viewCountry?: string
    /** What this user's country allows. Absent from an older backend; read it through `features/capabilities`, since a missing block and a missing key mean opposite things. */
  capabilities?: RawCapabilities
}

/** Partial user embedded in leases, properties, notifications. */
export interface UserRef {
  id: string
  firstName: string
  lastName: string
  mobile?: string
  email?: string
  avatar?: string
}

export function fullName(user: Pick<UserSession, 'firstName' | 'lastName'>): string {
  return `${user.firstName} ${user.lastName}`.trim()
}

/** GET /geo-records/countries item. `phoneCode` has no leading "+". */
export interface Country {
  id: string
  name: string
  iso2: string
  phoneCode: string
  emoji?: string
  /** ISO 4217 of the country's currency (geo_records/get_countries.go); a new property's amounts are in it. */
  currency?: string
  /** LeazePro is launched here (country_settings.enabled, LP-921). Absent from an older backend. */
  launched?: boolean
}

/** POST /v2/media/upload-url response: a presigned S3 POST. */
export interface MediaUploadData {
  s3Key: string
  url: string
  fields: Record<string, string>
  maxBytes?: number
}

export type MediaType =
  | 'avatar' | 'tenant_avatar' | 'property_banner' | 'property_gallery'
  | 'aadhaar_front' | 'aadhaar_back' | 'pan' | 'passport_photo'
  | 'electricity_bill' | 'tax_bill' | 'index2' | 'agreement_document' | 'maintenance_photo'
  | 'support_attachment'

export type FeatureFlagStatus = 'enabled' | 'disabled' | 'comingSoon' | 'deprecated'

export interface FeatureFlag {
  key: string
  name: string
  description: string
  defaultStatus: FeatureFlagStatus
  /** Effective status for the caller. */
  status: FeatureFlagStatus
}

/** One platform's release, as the backend sends it (snake_case). Builds and update type are mobile-only. */
export interface AppRelease {
  platform: string
  latest_version: string
  release_title?: string
  release_notes?: string[]
}

/** Keyed by platform; a platform is present only once an admin has set it up. The web app reads `web`. */
export type AppVersionConfig = Partial<Record<'android' | 'ios' | 'web', AppRelease>>

export type SubscriptionType = 'freemium' | 'standard' | 'professional' | 'unlimited'
export type SubscriptionStatus = 'created' | 'authenticated' | 'active' | 'pending' | 'halted' | 'cancelled' | 'completed' | 'expired'

export interface Subscription {
  id: string
  planId: string
  planTitle: string
  type: SubscriptionType
  price: number
  /** ISO 4217 of `price` and `leaseFeeBase` (the plan's price for the landlord's country, LP-492); absent from an older backend, which means INR. */
  currency?: Currency
  status: SubscriptionStatus
  canPublishPublicly: boolean
  leaseFeeBase?: number
  leaseFeeStepDownPct?: number
  leaseFeeFloorPct?: number
  /** Razorpay epoch seconds; absent on freemium. */
  currentStart?: number
  currentEnd?: number
  chargeAt?: number
  startAt?: number
  endAt?: number
  paidCount?: number
  totalCount?: number
  remainingCount?: number
  /** "paddle" for a Paddle subscription, managed in Paddle's portal (POST /v2/subscription/portal, LP-514). */
  provider?: 'paddle'
  /** A plan change waiting for the end of the paid period (LP-529): the paid plan stays until `effectiveAt` (epoch seconds),
   *  then becomes `planId` (only Freemium is ever scheduled). Absent when none is, and from a backend before LP-529. */
  scheduledChange?: { planId: string; effectiveAt: number }
}

/** GET /v2/subscription-status/:id — a plan change that is still being processed. */
export interface SubscriptionPendingStatus {
  subscriptionId: string
  status: SubscriptionStatus
  location: 'active' | 'pending'
  planId?: string
  planTitle?: string
  planType?: SubscriptionType
}

export type PaymentStatus = 'awaiting_payment' | 'pending' | 'processing' | 'completed' | 'failed'
export type PaymentGroupKey = 'standard' | 'premium' | 'offline' | 'upi_direct'
export type PaymentGateway = 'razorpay' | 'cashfree' | 'icici_eazypay' | 'upi_direct' | 'paddle'

export interface PaymentGroup {
  key: PaymentGroupKey
  label: string
  feePercent?: number
  methods?: string[]
  isOffline: boolean
  isUpiDirect: boolean
}

/** GET /payments/rent/fee-estimate → feeBreakdown (all amounts in rupees). */
export interface FeeBreakdown {
  paymentMethod?: string
  propertyType?: string
  rentAmount: number
  rentGstPercent?: number
  rentGstAmount?: number
  razorpayFeePercent?: number
  razorpayFeeGstPercent?: number
  razorpayFeeBaseAmount?: number
  razorpayFeeGstAmount?: number
  razorpayFeeTotalAmount?: number
  tenantRazorpayAmount?: number
  landlordRazorpayAmount?: number
  platformFee?: number
  platformFeeGst?: number
  platformFeeTotal?: number
  payoutFee?: number
  payoutFeeGst?: number
  payoutFeeTotal?: number
  tenantTotalFee?: number
  landlordTotalFee?: number
  tenantPayable: number
  landlordReceivable?: number
}

export interface FeeEstimate {
  feeBreakdown: FeeBreakdown
  /** JWT, ~15 minutes; required by POST /payments/rent for online groups. */
  feeToken: string
}

/** POST /payments/rent for an online group. Razorpay fields or Cashfree fields depending on `gateway`. */
export interface OnlinePaymentResponse {
  gateway: PaymentGateway
  transactionId: string
  /** paise */
  amount: number
  /** ISO 4217 of the order. Not sent today (the backend creates the order in the lease's currency); read first when it is. */
  currency?: Currency
  key?: string
  orderId?: string
  prefill?: Record<string, string>
  config?: Record<string, unknown>
  retry?: Record<string, boolean>
  sessionId?: string
  environment?: 'sandbox' | 'production'
}

export interface UpiDirectResponse {
  gateway: 'upi_direct'
  transactionId: string
  intentUrl: string
  vpa: string
  payeeName: string
  /** rupees */
  amount: number
}

/** Shared by the four confirm loops: status is "is it done", validate adds the recovery code. */
export interface PaymentConfirmStatus {
  confirmed: boolean
}
export type ValidateCode = 'confirmed' | 'waiting' | 'recovered' | 'timeout'
export interface PaymentValidateResult extends PaymentConfirmStatus {
  code: ValidateCode
  remainingSeconds: number
}

export interface RentPaymentStatus extends PaymentConfirmStatus {
  transactionId: string
  status: PaymentStatus
  gateway: string
}

/** POST /leases/fee-estimate */
/** One lease-year of a fee quote (LP-993): who pays it and what is still charged. `amount` is net of a code's `discount`. */
export interface LeaseFeeYear {
  year: number
  paidBy: 'charge' | 'credit' | 'free-lease' | 'override' | 'plan'
  listAmount: number
  amount: number
  /** What a promo code took off this year (LP-1001). */
  discount?: number
}

/** What a promo code did to a fee quote (LP-1001), or why it couldn't. */
export interface LeaseFeePromo {
  valid: boolean
  code: string
  discount?: { currency: string; amount: number }
  reason?: string
  message?: string
}

export interface LeaseFeeQuote {
  quantity: number
  amount: number
  free: boolean
  /** `credit`: every year paid by lease credits; `promo`: a code took the rest (LP-993, LP-1001). */
  freeReason?: 'override' | 'free-lease' | 'credit' | 'promo' | ''
  /** Year by year; absent from an older backend. `amount` is always their sum. */
  years?: LeaseFeeYear[]
  /** Lease-years the wallet pays for, and the wallet after them. */
  creditsApplied?: number
  creditsLeft?: number
  /** Only when the quote was asked with a `promoCode`. */
  promo?: LeaseFeePromo
  /** ISO 4217 of `amount`: the landlord's plan currency, which need not be the rent's (LP-492). Absent from an older backend; treat as INR. */
  currency?: Currency
}

/** POST /leases/:id/payment and POST /agreements/:id/payment (always Razorpay today). */
export interface RazorpayOrderInit {
  key: string
  gatewayOrderId: string
  /** paise */
  amount: number
  currency: string
  gateway: PaymentGateway
  paymentToken: string
  /** Paddle only (LP-514): `gatewayOrderId` is the Paddle transaction, opened by Paddle.js with this public token. */
  clientToken?: string
  /** Paddle and Cashfree: the mode their checkout opens in. */
  environment?: PaddleEnvironment
  /** Cashfree only (LP-521): the payment session its checkout opens; there is no Razorpay `key` then. */
  sessionId?: string
}

export type PaddleEnvironment = 'sandbox' | 'production'

export interface LeasePaymentInit extends RazorpayOrderInit {
  quantity: number
  total: number
}
export interface AgreementPaymentInit extends RazorpayOrderInit {
  breakdown: { stampDuty: number; platformFee: number; signingFee: number; gst: number; hardCopyFee: number; total: number }
}

/** POST /v2/subscription — a free plan returns only subscription_id. */
export interface SubscriptionUpgradeResponse {
  key?: string
  subscription_id: string
  name?: string
  description?: string
  prefill?: { contact?: string; email?: string }
  /** "paddle" when the plan is billed through Paddle (LP-514): open `transactionId` in Paddle.js; `subscription_id` is
   *  then LeazePro's own, for polling. Absent for Razorpay and free plans. */
  provider?: 'paddle'
  transactionId?: string
  clientToken?: string
  environment?: PaddleEnvironment
}
export type SubscriptionValidateCode = 'NO_ACTION' | 'WAITING_FOR_ACTIVATION' | 'ACTIVATED' | 'ACTIVATION_TIMEOUT'
export interface SubscriptionValidateResponse {
  activeSubscriptionId?: string
  pendingSubscriptionId?: string
  code: SubscriptionValidateCode
  remainingTime?: number
}

/** GET /rent-stats and the `rentStat` of GET /dashboard-stats. `overdue`/`upcoming` split `pending` at the caller's start of today. */
export interface RentStat extends StatScope {
  expected: number
  collected: number
  pending: number
  overdue?: number
  upcoming?: number
}

export interface PropertyStat {
  total: number
  occupied: number
  vacant: number
}

export type PaymentCategory = 'rent' | 'deposit' | 'lease' | 'agreement' | 'subscription' | 'other'
export type PaymentMode = 'online' | 'offline'

/** One payment row as GET /dashboard-stats (latest 5 received) and GET /v2/payments project it. */
export interface Transaction {
  id: string
  /** Lease id for rent/deposit, plan id for subscriptions. */
  itemId?: string
  /** Property id for rent/deposit. */
  subItemId?: string
  amount: number
  /** ISO 4217, set by the backend. Absent on records written before it; treat as INR. */
  currency?: Currency
  category: PaymentCategory
  mode?: PaymentMode
  method?: 'cash' | 'upi' | 'card' | 'netbanking' | 'wallet' | 'emi' | 'paylater'
  status: PaymentStatus
  note?: string
  failureReason?: string
  metadata?: TransactionMetadata
  payer?: UserRef
  payee?: UserRef
  createdAt: string
}

/** Transaction metadata (partial_models.go). Only the branch for the category is present. */
export interface RentMeta {
  /** ISO dates of the rents this payment covers. */
  rentForDates?: string[]
  feeBreakdown?: FeeBreakdown
  processedRents?: { ids: string[]; transactionId: string; paidAmount: number; status: string }[]
  upiIntentUrl?: string
}
export interface TransactionMetadata {
  rentMeta?: RentMeta
  leaseMeta?: { leaseId: string; quantity: number; total: number }
  agreementMeta?: Record<string, unknown>
  subscriptionMeta?: Record<string, unknown>
}

export interface TenantWithDue extends UserRef {
  due: number
  paymentPending?: boolean
}

/** What a combined statistic covers (LP-491): the one country of every figure in it and the currency they are in. */
export interface StatScope {
  country?: string
  currency?: Currency
}

export interface DashboardStats extends StatScope {
  unreadActivities?: number
  rentStat: RentStat
  totalTenantsWithDue: number
  tenantsWithDue: TenantWithDue[]
  propertyStat: PropertyStat
  payments: Transaction[]
}

export interface RentTrendMonth {
  /** `YYYY-MM` in the caller's timezone. */
  month: string
  expected: number
  collected: number
  overdue: number
}

export interface RentTrend extends StatScope {
  months: RentTrendMonth[]
}

export type AttentionKind = 'rent_due' | 'kyc_action' | 'agreement_pending' | 'maintenance_open' | 'interest_new'
export type AttentionScreen = 'tenants' | 'lease-detail' | 'agreement-detail' | 'property-interests' | 'banking'

export interface AttentionTarget {
  screen: AttentionScreen
  id?: string
  leaseId?: string
}

export interface AttentionPreview {
  id: string
  title?: string
  /** Agreement reason (`payment-pending` | `signing-failed` | `landlord-sign-pending`) or maintenance priority. */
  reason?: string
  amount?: number
  at?: string
  target?: AttentionTarget
}

export interface AttentionItem {
  kind: AttentionKind
  count: number
  /** rent_due: the overdue total when every lease shares one currency (with `currency`); else absent and `amounts` set. */
  amount?: number
  currency?: string
  /** rent_due across currencies: one entry per currency, instead of `amount` (LP-580). */
  amounts?: { currency: string; amount: number }[]
  /** KYC: `not_started` | `pending` | `failed`. */
  reason?: string
  preview: AttentionPreview[]
  target?: AttentionTarget
}

export interface AttentionFeed {
  items: AttentionItem[]
  /** Sources that failed inside the backend's per-source timeout; the client must not say "all caught up" while this is non-empty. */
  unavailable?: AttentionKind[]
}

export interface AccountStats extends StatScope {
  totalProperty: number
  totalTenants: number
  averageRent: number
}

export type PropertyType = 'residential' | 'commercial' | 'industrial' | 'institutional' | 'land'
export type RentFrequency = 'daily' | 'weekly' | 'monthly' | 'annually'
export type PropertyOccupancy = 'vacant' | 'occupied'
export type PublicVisibilityStatus = 'hidden' | 'pending' | 'approved' | 'rejected'

export const PROPERTY_TYPES: PropertyType[] = ['residential', 'commercial', 'industrial', 'institutional', 'land']
export const RENT_FREQUENCIES: RentFrequency[] = ['daily', 'weekly', 'monthly', 'annually']

/** Spec values are strings: booleans `"true"`/`"false"`, numbers as digit strings, selects the option value. */
export interface PropertySpec {
  key: string
  label?: string
  value: string
}

export interface GalleryImage {
  /** Full S3 key. */
  key: string
  isPrimary?: boolean
}

export interface PublicVisibility {
  status: PublicVisibilityStatus
  requestedAt?: string
  reviewedAt?: string
  reviewedBy?: string
  rejectionReason?: string
}

/** The lease the property list/detail join in: the owner's most recent `active` or `payment-pending` lease. */
export interface PropertyLease {
  id: string
  startDate: string
  endDate: string
  status: 'active' | 'payment-pending' | (string & {})
  tenant?: UserRef
  rent?: number
  deposit?: number
  frequency?: RentFrequency
  propertyType?: PropertyType
  fee?: number
  /** ISO 4217 of `fee` (the landlord's plan currency, not the rent's); absent means INR. */
  feeCurrency?: Currency
  feeQuantity?: number
}

export interface PropertyAddress extends Address {
  locality?: string
}

/** GET /properties item (list projection) and GET /property/:id (detail projection) share this shape; absent fields are simply undefined. */
export interface Property {
    /** What may be done about this record in its country; absent from an older backend, which reads as everything allowed (see `readRecordCapabilities`). */
  capabilities?: RawCapabilities

  id: string
  title: string
  bannerImage?: string
  type?: PropertyType
  rent?: number
  /** ISO 4217, set by the backend. Absent on records written before it; treat as INR. */
  currency?: Currency
  deposit?: number
  frequency?: RentFrequency
  address?: PropertyAddress
  specs?: PropertySpec[]
  galleryImages?: GalleryImage[]
  publicVisibility?: PublicVisibility
  lease?: PropertyLease
  archived?: boolean
  /** List only; the detail derives it from `lease`. */
  status?: PropertyOccupancy
}

export interface PropertyListResponse {
  properties: Property[]
  total: number
  page: number
  size: number
}

export interface PropertyAddressInput {
  firstLine: string
  secondLine?: string
  city: string
  state: string
  stateCode: string
  country: string
  countryCode: string
  postalCode: string
  locality?: string
}

/** POST /property body. `stateCode` and `countryCode` are required by the binder even though the docs omit them. */
export interface PropertyCreateInput {
  title: string
  type: PropertyType
  rent: number
  frequency: RentFrequency
  deposit?: number
  address: PropertyAddressInput
  bannerImage?: string
  specs?: PropertySpec[]
  galleryImages?: GalleryImage[]
}

/** PUT /property/:id body: every field optional; empty/zero scalars are ignored by the backend, and `specs: []` / `galleryImages: []` are no-ops. */
export interface PropertyUpdateInput extends Partial<Omit<PropertyCreateInput, 'address'>> {
  address?: Partial<PropertyAddressInput>
}

export type PropertyAttributeInput = 'number' | 'text' | 'select' | 'boolean'

/** GET /property-attributes?propertyType= item. */
export interface PropertyAttribute {
  id: string
  key: string
  label: string
  propertyTypes: PropertyType[]
  inputType: PropertyAttributeInput
  options?: { value: string; label: string }[]
  unit?: string
  enabled?: boolean
}

/** GET /property-stats?propertyId= — `propertyStat.overdue/upcoming` are always 0 on this endpoint. */
export interface PropertyStats {
  leaseStat: { collected: number; expected: number }
  propertyStat: { collected: number; expected: number; overdue?: number; upcoming?: number }
  totalTenants: number
}

/** GET /geo-records/search-cities item. */
export interface CityRecord {
  city: string
  state: string
  stateCode: string
  country: string
  countryCode: string
}

export type LeaseStatus = 'active' | 'canceled' | 'expired' | 'cancellation-requested' | 'payment-pending'

// Rents paid / paid-partially / scheduled / pending. List: `expected` = due so far, `totalExpected` = whole term; detail: both equal due-so-far.
// `collected` includes a tenant's offline payment the landlord has not accepted (status `pending`), on purpose, so the tenant sees no due for it.
export interface LeaseStat {
  totalExpected: number
  expected: number
  collected: number
  /** The part of `collected` a tenant recorded that the landlord has not accepted. Absent from older backends. */
  pendingConfirmation?: number
}

export interface LeaseProperty {
  id: string
  title: string
  bannerImage?: string
  type?: PropertyType
  deposit?: number
  rent?: number
  frequency?: RentFrequency
  address?: PropertyAddress
  archived?: boolean
  documents?: Record<string, { key: string; updatedAt?: string }>
}

export interface LeaseTenant extends UserRef {
  address?: Address
}

/** GET /v2/leases item and GET /leases/:id. `fee`/`feeQuantity`/`landlord` only on the detail. */
export interface Lease {
    /** What may be done about this record in its country; absent from an older backend, which reads as everything allowed (see `readRecordCapabilities`). */
  capabilities?: RawCapabilities

  id: string
  tenant?: LeaseTenant
  landlord?: UserRef
  property?: LeaseProperty
  startDate: string
  endDate: string
  deposit: number
  rent: number
  /** ISO 4217, set by the backend. Absent on records written before it; treat as INR. */
  currency?: Currency
  frequency: RentFrequency
  propertyType?: PropertyType
  status: LeaseStatus
  /** ISO 8601 UTC instant the lease was released; only on a canceled lease released after LP-630 (LP-845). */
  canceledDate?: string
  stat?: LeaseStat
  archived?: boolean | null
  fee?: number
  /** ISO 4217 of `fee` (the landlord's plan currency, not the rent's `currency`); absent means INR. */
  feeCurrency?: Currency
  feeQuantity?: number
  createdAt?: string
  updatedAt?: string
}

export interface LeaseFacets {
  all: number
  active: number
  endingSoon: number
  paymentPending: number
}

export interface LeaseListResponse {
  items: Lease[]
  total: number
  page: number
  size: number
  facets?: LeaseFacets
}

/** POST /leases body. `duration` counts rent periods of `frequency`; `tenantMobile` is "<cc> <number>". */
export interface LeaseCreateInput {
  tenantMobile: string
  /** ISO2 of the country picked for the tenant's phone; optional because `+1` alone cannot say US or Canada. Omitted when none was picked. */
  tenantCountryCode?: string
  tenantAlternateMobile?: string
  tenantFirstName: string
  tenantLastName: string
  /** File name (e.g. "avatar.jpg"); the response carries a presigned upload for a new tenant. */
  tenantAvatar?: string
  propertyId: string
  duration: number
  startDate: string
  frequency: RentFrequency
  rent: number
  deposit?: number
  propertyType: PropertyType
  /** A lease-fee promo code (LP-1001); a code that can't be used refuses the create with 409. */
  promoCode?: string
}

export interface LeaseCreateResponse {
  leaseId: string
  tenant: UserRef & { alternateMobile?: string; profileCompleted?: boolean }
  tenantAvatarUpload?: MediaUploadData
  status: 'active' | 'payment-pending'
  fee: LeaseFeeQuote
}

/** GET /tenant?mobile= — an existing user by mobile. */
export interface TenantLookup extends UserRef {
  alternateMobile?: string
  address?: Address
}

export type RentStatus = 'paid' | 'paid-partially' | 'scheduled' | 'pending' | 'canceled'

/** GET /leases/:id/rents item. Paid/part-paid/canceled for all time; scheduled/pending up to the end of next month. */
export interface Rent {
  id: string
  rentDate: string
  expectedAmount: number
  paidAmount: number
  status: RentStatus
  paymentMode?: PaymentMode
  transactionId?: string
  receiptUrl?: string
  updatedAt?: string
  transactionStatus?: PaymentStatus
  transactionMode?: PaymentMode
  transactionMethod?: string
  transactionAmount?: number
  /** e.g. ["January 2026"] — the months the settling transaction covered. */
  rentForMonths?: string[]
  /** The part of `paidAmount` a tenant claimed that the landlord has not accepted (status `pending`). Not yet sent by `/leases/:id/rents`. */
  heldAmount?: number
}

export interface RentListResponse {
  items: Rent[]
  total: number
  totalRents?: number
}

export type PaymentListFilter = 'all' | 'received' | 'pending' | 'failed'

export interface PaymentFacets {
  all: number
  received: number
  pending: number
  failed: number
}

/** `YYYY-MM` buckets in the caller's timezone; follow `filter`, ignore `search`; at most 24. */
export interface PaymentMonth {
  month: string
  total: number
  count: number
}

export interface PaymentListResponse {
  items: Transaction[]
  total: number
  page: number
  size: number
  months?: PaymentMonth[]
  facets?: PaymentFacets
}

export interface TenantLeaseDues {
  leaseId: string
  overdueAmount: number
  overdueCount: number
  oldestOverdueDate?: string
  nextDueDate?: string
  nextDueAmount?: number
}

/** A lease as the tenant dashboard projects it (no tenant; `property` is `{id, title}` plus whatever else is stored). */
export interface TenantLease extends Lease {
  nextRentDueDate?: string
}

/** GET /tenant/interests row. */
export type TenantInterestStatus = 'pending' | 'acknowledged' | 'declined'

export interface TenantInterest {
  id: string
  propertyId: string
  /** Absent once the property was deleted; the row stays so the history does not shrink. */
  property?: { title: string; address?: { locality?: string; city?: string; state?: string; postalCode?: string } }
  status: TenantInterestStatus
  message?: string
  acknowledgedAt?: string
  declinedAt?: string
  createdAt: string
}

export interface TenantInterestListResponse {
  interests: TenantInterest[]
  total: number
  page: number
  size: number
}

export interface TenantDashboardStats extends StatScope {
  firstName?: string
  avatar?: string
  unreadActivities?: number
  /** Up to 100, never `payment-pending`. */
  leases: TenantLease[]
  /** Latest 5 the tenant paid, newest first; `property` is joined. */
  payments: (Transaction & { property?: { id: string; title: string } })[]
  /** Absent → estimate from `stat`; `[]` → nothing owed; a lease with no entry → paid. */
  dues?: TenantLeaseDues[]
}

export type MaintenanceStatus = 'open' | 'acknowledged' | 'in_progress' | 'resolved'
export type MaintenancePriority = 'low' | 'normal' | 'high' | 'urgent'
export type MaintenanceTab = 'all' | 'open' | 'inProgress' | 'resolved'

export interface MaintenanceCategory {
  id: string
  name: string
  description?: string
  isActive?: boolean
  order?: number
}

export interface MaintenanceRequest {
  id: string
  leaseId: string
  propertyId?: string
  tenantId?: string
  landlordId?: string
  categoryId: string
  categoryName?: string
  priority: MaintenancePriority
  title: string
  description?: string
  /** Signed URLs (24 h); refetch rather than cache. */
  photoUrls?: string[]
  status: MaintenanceStatus
  landlordNote?: string
  /** "MTN-YYYYMMDD-XXXXXX" */
  referenceId?: string
  acknowledgedAt?: string
  inProgressAt?: string
  resolvedAt?: string
  createdAt: string
  updatedAt?: string
  /** Landlord-wide list and admin only. */
  tenant?: UserRef
  property?: { id: string; title: string }
}

export interface MaintenanceFacets {
  all: number
  open: number
  inProgress: number
  resolved: number
}

export interface MaintenanceListResponse {
  items: MaintenanceRequest[]
  total: number
  facets?: MaintenanceFacets
}

export interface MaintenanceCreateInput {
  categoryId: string
  title: string
  description?: string
  priority: MaintenancePriority
  /** S3 keys from `maintenance_photo` uploads; at most 3. */
  photoKeys?: string[]
}

export type BrowseSort = 'newest' | 'rentAsc' | 'rentDesc' | 'vacantFirst'
export type BrowseAvailability = 'vacant' | 'occupied' | 'vacantByNextMonth'
export type PropertyInterestStatus = 'pending' | 'acknowledged' | 'declined'

export interface LandlordContact {
  firstName: string
  lastName: string
  mobile?: string
  email?: string
  avatar?: string
}

/** GET /public/properties item and GET /public/property/:id (approved, not archived/deleted). */
export interface PublicProperty {
  id: string
  title: string
  bannerImage?: string
  galleryImages?: GalleryImage[]
  type?: PropertyType
  rent?: number
  /** ISO 4217 of rent and deposit (public_listings/listing.go); absent means INR. */
  currency?: Currency
  deposit?: number
  frequency?: RentFrequency
  address?: PropertyAddress
  specs?: PropertySpec[]
  publicVisibility?: PublicVisibility
  isOccupied?: boolean
  /** Only when occupied. */
  leaseEndsOn?: string
  interestShown?: boolean
  interestAcknowledged?: boolean
  /** The landlord declined this tenant's interest; `interestShown` stays true. */
  interestDeclined?: boolean
  /** Detail only, and only once the tenant's interest is acknowledged. */
  landlordContact?: LandlordContact
}

export interface PublicPropertyListResponse {
  properties: PublicProperty[]
  total: number
  page: number
  size: number
}

export type AgreementStatus = 'draft' | 'payment-pending' | 'pending-sign' | 'partially-signed' | 'signing-failed' | 'signed' | 'pending-stamp' | 'stamped' | 'completed'
export type AgreementStageKey = 'draft' | 'payment' | 'stamping' | 'signing' | 'ready'
export type SignStatus = '' | 'pending' | 'signed' | 'rejected' | 'expired' | 'declined'
export type PropertyDocumentType = 'electricity_bill' | 'tax_bill'

export interface AgreementParty {
  name?: string
  address?: string
  email?: string
  mobile?: string
  kycId?: string
  passportPhotoKey?: string
  aadhaarLast4?: string
  signUrl?: string
  signedAt?: string
  signStatus?: SignStatus
  failedReason?: string
}

export interface AgreementCharges {
  stampDuty: number
  platformFee: number
  signingFee: number
  gst: number
  hardCopyAvailable?: boolean
  hardCopyFee?: number
  totalWithoutHardCopy: number
  totalWithHardCopy?: number
}

export interface Agreement {
  id: string
  leaseId: string
  landlordId?: string
  tenantId?: string
  propertyId?: string
  status: AgreementStatus
  documentType?: string
  propertyType?: PropertyType
  stateCode?: string
  landlord?: AgreementParty
  tenant?: AgreementParty
  witnesses?: AgreementParty[]
  landlordConsent?: { isPropertyOwner: boolean; consentedAt?: string }
  tenantConsent?: { isAdult: boolean; consentedAt?: string }
  property?: { address?: string; city?: string; postalCode?: string; stateCode?: string; country?: string; documentKey?: string; documentType?: PropertyDocumentType; index2Key?: string }
  rentAmount?: number
  depositAmount?: number
  rentFrequency?: string
  durationMonths?: number
  startDate?: string
  endDate?: string
  noticePeriodDays?: number
  charges?: AgreementCharges
  paidAt?: string
  stampedAt?: string
  eSignInitiatedAt?: string
  hardCopyRequested?: boolean
  documentSource?: 'uploaded'
  uploadedFileName?: string
  uploadedS3Key?: string
  createdAt: string
  updatedAt?: string
}

export interface AgreementStage {
  key: AgreementStageKey
  label: string
  isCompleted: boolean
  isActive: boolean
}

/** GET /agreements/for-lease, GET /agreement/:id, POST /leases/:id/agreements/upload. */
export interface AgreementView {
  agreement: Agreement | null
  /** ~15-minute presigned URL of the best document (signed → stamped → draft, or the upload). */
  documentUrl?: string
  canDownload?: boolean
  currentStage?: AgreementStageKey
  stages?: AgreementStage[]
  /** GET /agreements/for-lease only (LP-489): whether a new agreement can be created for the property's country. Absent means allowed. */
  agreementsAllowed?: boolean
}

export interface AgreementRule {
  stateCode?: string
  stateName: string
  witnessCount: number
  /** 0 = no limit */
  maxDurationMonths: number
  noticePeriodDays: number
  lockInPeriodMonths?: number
  policeVerificationRequired?: boolean
  notarizationRequired?: boolean
  eStampingAvailable?: boolean
  applicableAct?: string
  platformFee?: number
  signingFee?: number
  hardCopyAvailable?: boolean
  hardCopyFee?: number
  preferredDocumentType?: string
}

export interface KycEntry {
  kycId: string
  name: string
  address?: string
  /** masked */
  aadhaarNumber?: string
  passportPhotoKey?: string
}

/** GET /agreements/prefill?leaseId= */
export interface AgreementPrefill {
  landlord: KycEntry[]
  tenant: KycEntry[]
  witnesses: KycEntry[]
  property: { id: string; address: { line1?: string; line2?: string; city?: string; state?: string; stateCode?: string; postalCode?: string; country?: string; countryCode?: string }; electricityBill?: { key: string; updatedAt?: string }; taxBill?: { key: string; updatedAt?: string }; index2?: { key: string; updatedAt?: string } }
  lease: { rent: number; deposit: number; frequency: string; startDate?: string; durationMonths?: number }
}

export interface AgreementDraftInput {
  leaseId: string
  landlordKycId: string
  tenantKycId: string
  witnesses: { kycId: string; email?: string; mobile?: string }[]
  landlordConsent: { isPropertyOwner: true; consentedAt: string }
  tenantConsent: { isAdult: true; consentedAt: string }
  rentAmount: number
  depositAmount: number
  rentFrequency: string
  /** YYYY-MM-DD */
  startDate: string
  durationMonths: number
  noticePeriodDays: number
  propertyDocumentType: PropertyDocumentType
}

export interface AgreementSigner {
  name: string
  email?: string
  role: string
  signed: boolean
  rejected?: boolean
  expired?: boolean
  failedReason?: string
}

export interface AgreementSignStatus {
  agreementId: string
  agreementStatus: AgreementStatus
  confirmed: boolean
  signers: AgreementSigner[]
  code?: ValidateCode
  remainingSeconds?: number
}

export interface PropertyInterest {
  id: string
  propertyId: string
  property?: { title: string; address?: { locality?: string; city?: string; state?: string; postalCode?: string } }
  tenantId: string
  tenantName: string
  tenantPhone?: string
  status: PropertyInterestStatus
  /** The tenant's optional note with the interest, up to 280 characters. */
  message?: string
  acknowledgedAt?: string
  declinedAt?: string
  createdAt: string
}

export interface PropertyInterestListResponse {
  interests: PropertyInterest[]
  total: number
  page: number
  size: number
}

/** The 19 backend types (`constants/notification_types.go`); anything else is `unknown`. */
export const NOTIFICATION_TYPES = [
  'lease.created',
  'rent.paid.by_tenant',
  'rent.offline.pending_acceptance',
  'rent.offline.accepted',
  'rent.paid.by_landlord',
  'agreement.initiated',
  'lease.cancellation.requested',
  'lease.cancelled',
  'rent.reminder',
  'property.interest.shown',
  'property.interest.acknowledged',
  'property.interest.declined',
  'rent.cron.due_today',
  'rent.cron.overdue_reminder',
  'rent.cron.due_soon',
  'rent.cron.overdue_landlord',
  'rent.cron.offline_pending',
  'maintenance.raised',
  'maintenance.status_update',
] as const
export type NotificationType = (typeof NOTIFICATION_TYPES)[number] | 'unknown'

/** The eight `screen` values the backend sends (`services/notification_v2/{events,property,support}.go`). */
export type NotificationScreen = 'lease-detail' | 'payment-detail' | 'agreement-detail' | 'property-detail' | 'payments' | 'property-interests' | 'tenant-dashboard' | 'support-request'

export interface NotificationData {
  screen?: NotificationScreen | string
  leaseId?: string
  propertyId?: string
  transactionId?: string
  agreementId?: string
  maintenanceId?: string
  /** The support request a `support.reply` / `support.resolved` is about (LP-925). */
  requestId?: string
}

export interface AppNotification {
  id: string
  type: NotificationType | string
  title: string
  body: string
  data?: NotificationData
  read: boolean
  createdAt: string
}

export interface NotificationListResponse {
  notifications: AppNotification[]
  total: number
  page: number
  size: number
}

export type PlanFrequency = 'monthly' | 'annually'

/** GET /subscription-plans (sorted by rank; retired plans hidden from non-admins). */
export interface SubscriptionPlan {
  id: string
  title: string
  type: SubscriptionType
  description?: string
  audience?: string
  features?: string[]
  rank: number
  price: number
  /** ISO 4217 of `price` and `leaseFeeBase`, resolved by the backend for the landlord's country (LP-492); absent means INR. */
  currency?: Currency
  /** Paid plans: the countries this plan is sold in. The list is already filtered to the caller's country. */
  countries?: string[]
  unit?: string
  frequency: PlanFrequency
  /** The lease fee curve (`lease/pricing.go`): block 1 = base, each later year steps down, floored. */
  leaseFeeBase: number
  leaseFeeStepDownPct: number
  leaseFeeFloorPct: number
  /** Lifetime free leases; the backend treats unset as 1. */
  freeLeases?: number
  canPublishPublicly?: boolean
  deleted?: boolean
  /** Who bills this paid plan (LP-514): "paddle", or absent for Razorpay. */
  provider?: 'paddle'
}

/** PUT /profile body: partial, only what changes. */
export interface ProfileUpdateInput {
  firstName?: string
  lastName?: string
  email?: string
  avatar?: string
  currency?: Currency
  timeZone?: string
  /** Which language the server writes notifications in. Not the UI language, which is local. */
  language?: 'en' | 'hi'
  /** The role switch — the response then carries a new token pair. */
  defaultProfile?: ProfileType
  address?: Omit<Address, 'status'>
  /** ISO2 of the viewing country (LP-491); 400 `viewCountry is invalid` for an unknown one. */
  viewCountry?: string
}

/** GET /account-stats (landlord). */
export interface AccountStats extends StatScope {
  totalProperty: number
  totalTenants: number
  averageRent: number
}

/** Where a KYC step stands. `notStarted` = no record yet. */
export type KycStatus = 'notStarted' | 'pending' | 'processing' | 'completed' | 'failed'
export type KycStep = 'address' | 'document' | 'bank'

/** GET /profile/notification-preferences — one push group. */
export interface NotificationPrefGroup {
  key: string
  enabled: boolean
}

export type DocumentType = 'aadhaar' | 'pan'
export type KycRecordType = 'own-kyc' | 'legal-landlord-kyc' | 'witness-kyc'
export type DocumentSideStatus = 'pending' | 'verified' | 'failed'

export interface DocumentKycSide {
  cashFreeRefId?: string
  status: DocumentSideStatus | string
  failReason?: string
  submittedAt?: string
  verifiedAt?: string
}

/** One identity-document record (`DocumentKYC`). Aadhaar numbers arrive masked. */
export interface DocumentKyc {
  id: string
  userId?: string
  documentType: DocumentType
  kycType?: KycRecordType
  fullName?: string
  dob?: string
  gender?: string
  address?: { raw?: string; district?: string; state?: string; postCode?: string }
  aadhaarNumber?: string
  panNumber?: string
  passportPhotoKey?: string
  aadhaarFront?: DocumentKycSide
  aadhaarBack?: DocumentKycSide
  pan?: DocumentKycSide
  status: 'pending' | 'verified' | 'failed' | string
  createdAt?: string
  updatedAt?: string
}

export interface DocumentVerifyInput {
  documentType: DocumentType
  kycType: KycRecordType
  aadhaarFrontKey?: string
  aadhaarBackKey?: string
  panKey?: string
}

export type BankAccountStatus = 'VERIFIED' | 'INITIATED' | 'INVALID' | 'CANCELLED' | 'FAILED' | 'DELETED'

/** A payout instrument (`LandlordBanking`): a UPI VPA today, bank accounts on older records. */
export interface BankAccount {
  id: string
  accountHolderName: string
  /** masked */
  accountNumber?: string
  ifsc?: string
  vpa?: string
  bankName?: string
  status: BankAccountStatus | string
  failReason?: string
  isDefault: boolean
  verifiedAt?: string
  createdAt?: string
}

export interface BankAccountListResponse {
  accounts: BankAccount[] | null
  total: number
  page: number
  size: number
}

export interface BankingStatus {
  status: 'not_started' | 'pending' | 'processing' | 'verified' | 'failed' | string
  hasVerifiedBanking: boolean
  hasVerifiedUpi: boolean
}

export interface SupportCategory {
  id: string
  name: string
  description?: string
  userTypes?: ProfileType[]
  /** When false the description is optional; "Other" always needs one. */
  allowsCustom?: boolean
  isActive?: boolean
  order?: number
}

export type SupportRequestStatus = 'new' | 'in_progress' | 'resolved' | 'closed'

export interface SupportRequest {
  id: string
  categoryId?: string
  categoryName: string
  description?: string
  status: SupportRequestStatus | string
  /** SUP-YYYYMMDD-XXXXXX — what a person quotes when they chase a request. */
  referenceId: string
  /** Signed read links for the files attached when raising it, in upload order. */
  attachmentUrls?: string[]
  /** The support team's replies, oldest first (LP-925); `[]` when nobody has answered. Absent from a backend before LP-925. */
  messages?: SupportMessage[]
  createdAt: string
  updatedAt?: string
  resolvedAt?: string | null
}

/** One reply on a request. Only the team writes them; the user's words are the request's description. */
export interface SupportMessage {
  id: string
  /** The admin who wrote it. Shown to the user as "LeazePro Support", never by name. */
  by: { id: string; name: string; role: string }
  body: string
  at: string
}

export interface SupportRequestListResponse {
  items: SupportRequest[]
  total: number
  page: number
  size: number
}

export const BHK_OPTIONS = ['1 RK', '1', '2', '3', '4+'] as const

/** The "looking for a rental" card (own or public). `tenantId` is never serialised. */
export interface TenantProfile {
  id: string
  name: string
  profession?: string
  city: string
  familySize?: number
  budget?: number
  /** Free text: "1", "2", "3+", "1 RK", "Studio"… */
  bhkPreference?: string
  /** ISO timestamp (or yyyy-mm-dd) */
  moveInDate?: string
  requirements?: string[]
  published: boolean
  /** Signed link on the own reads; a path relative to the API base on the public reads. Absent without a photo. */
  avatarUrl?: string
  createdAt?: string
  updatedAt?: string
}

/** PUT /tenant-profile — create or replace. `name` needs a first and a last name; `city` is required. */
export interface TenantProfileInput {
  name: string
  profession?: string
  city: string
  familySize?: number
  budget?: number
  bhkPreference?: string
  /** yyyy-mm-dd; omitted when none */
  moveInDate?: string
  requirements?: string[]
  published: boolean
  /** s3Key from an `avatar` upload; omitted keeps the photo, "" removes it. */
  avatar?: string
}

/** One signed-in session from `GET /sessions` (LP-943, frozen LP-938 contract). */
export type SessionPlatform = 'ios' | 'android' | 'web' | 'admin'
export type SessionEndReason = 'logout' | 'revoked_by_user' | 'revoked_by_admin' | 'expired' | 'account_deleted'
export interface DeviceSession {
  sid: string
  userId: string
  deviceId?: string
  platform?: SessionPlatform | string
  appVersion?: string
  deviceModel?: string
  osVersion?: string
  /** Masked (a.b.x.x) for everyone but admins. */
  ip?: string
  createdAt: string
  lastSeenAt?: string
  endedAt?: string
  endReason?: SessionEndReason
  /** The session making this request: it cannot be revoked here (log out instead). */
  current: boolean
}

/** GET /referrals/me item (LP-994): someone who signed up with this user's link. `mobile` is always masked. */
export interface ReferralItem {
  firstName: string
  mobile: string
  createdAt: string
  status: 'completed' | 'blocked'
  creditsEarned: number
}

/** GET /referrals/me. `active` is the flag and the program both on: show refer & earn only then. */
export interface ReferralSummary {
  inviteUrl: string
  active: boolean
  rewards: { referrerCredits: number; referredCredits: number; leaseYearsPerCredit: number }
  balance: number
  completed: number
  blocked: number
  items: ReferralItem[]
  total: number
  page: number
  size: number
}

export type WalletEntryType = 'earned' | 'welcome' | 'granted' | 'spent' | 'released' | 'revoked' | 'expired'

/** GET /wallet item (LP-990). `leaseYears` is signed: positive adds to the balance. */
export interface WalletEntry {
  id: string
  type: WalletEntryType
  leaseYears: number
  leaseId?: string
  reason?: string
  expiresAt?: string
  createdAt: string
}

/** What a promo code takes off (LP-999). `amount` on a percentage subscription code is what it takes off the plan. */
export interface PromoDiscount {
  kind: 'percentage' | 'flat'
  percentOff?: number
  currency?: string
  amount?: number
}

/** POST /promo-codes/validate. A usable subscription code carries the prices; one that can't be used, `reason` + `message`. */
export interface PromoValidation {
  valid: boolean
  code: string
  type?: 'subscription' | 'lease_fee'
  duration?: 'first_year' | 'every_year'
  discount?: PromoDiscount
  currency?: string
  listPrice?: number
  priceAfter?: number
  renewalPrice?: number
  renewalDate?: string
  reason?: string
  message?: string
}

/** GET /offers item: a featured code, priced on what it applies to in the country. */
export interface PromoOffer {
  code: string
  type: 'subscription' | 'lease_fee'
  duration?: 'first_year' | 'every_year'
  discount: PromoDiscount
  validTo?: string
  plans?: { planId: string; title: string; currency: string; listPrice: number; offerPrice: number; renewalPrice: number }[]
  leaseFee?: { currency: string; listPrice: number; offerPrice: number }
}
